import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Logger from "../../services/Logger";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ApiService from "../../services/ApiService";
import UtilService from "../../services/UtilService";
import StatsByStateTable from "../pzl_common/stats/StatsByStateTable";
import EditorsPicksOverview from "./EditorsPicksOverview";
import PuzzleOverview from "./PuzzleOverview";

const TYPE_LABELS = {
    WORD_SEARCH: "Word Search",
    HIDDEN_MESSAGE: "Hidden Message",
    CROSSWORD: "Crossword",
    WORD_WHEEL: "Word Wheel",
    SLIDING: "Sliding",
    JIGSAW: "Jigsaw",
    STANDALONE_IMAGE: "Standalone Image",
};
const TYPE_ORDER = ["CROSSWORD", "HIDDEN_MESSAGE", "WORD_SEARCH", "WORD_WHEEL", "SLIDING", "JIGSAW"];
const PUBLISH_TYPE_ORDER = [...TYPE_ORDER, "STANDALONE_IMAGE"];
const LANG_ORDER = ["english", "french"];
const LANG_LABELS = { english: "English", french: "French" };
const HIDE_FRENCH_KEY = "admin.hideFrench";

function readHideFrenchPreference() {
    try {
        const stored = localStorage.getItem(HIDE_FRENCH_KEY);
        if (stored === null) return true;
        return stored === "true";
    } catch {
        return true;
    }
}

function formatPublishDateWithHumanize(iso) {
    if (!iso) return { line1: "—", line2: null };
    try {
        const d = new Date(iso);
        const dateTimeStr = d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
        const diffMs = Date.now() - d.getTime();
        if (diffMs < 0) return { line1: dateTimeStr, line2: null };
        const humanized = UtilService.formatHumanizeDetailed(diffMs);
        return humanized ? { line1: humanized, line2: `(${dateTimeStr})` } : { line1: dateTimeStr, line2: null };
    } catch {
        return { line1: iso, line2: null };
    }
}

function orderedPublishDateRanges(ranges) {
    if (!Array.isArray(ranges) || ranges.length === 0) return [];
    return [...ranges].sort((a, b) => {
        const ta = PUBLISH_TYPE_ORDER.indexOf(a.type);
        const tb = PUBLISH_TYPE_ORDER.indexOf(b.type);
        if (ta !== tb) return (ta < 0 ? 999 : ta) - (tb < 0 ? 999 : tb);
        const la = LANG_ORDER.indexOf(a.language);
        const lb = LANG_ORDER.indexOf(b.language);
        return (la < 0 ? 999 : la) - (lb < 0 ? 999 : lb);
    });
}

function sumPlayRecordCounts(countsByType) {
    if (!countsByType || typeof countsByType !== "object") return 0;
    return TYPE_ORDER.reduce((sum, type) => sum + (Number(countsByType[type]) || 0), 0);
}

export default function WelcomeMain() {
    const [hideFrench, setHideFrench] = useState(readHideFrenchPreference);
    const [sessionData, setSessionData] = useState(null);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [englishStats, setEnglishStats] = useState(null);
    const [frenchStats, setFrenchStats] = useState(null);
    const [statsByStateLoading, setStatsByStateLoading] = useState(true);
    const [publishDateRanges, setPublishDateRanges] = useState([]);
    const [playRecordStats, setPlayRecordStats] = useState(null);
    const [playRecordLoading, setPlayRecordLoading] = useState(true);

    useEffect(() => {
        loadSessionCount();
    }, []);

    useEffect(() => {
        const loadPlayRecordStats = async () => {
            setPlayRecordLoading(true);
            try {
                const data = await ApiService.getData("/v1/stats/play-record");
                setPlayRecordStats(data);
            } catch (err) {
                Logger.error("Error loading play record stats:", err);
                setPlayRecordStats(null);
            } finally {
                setPlayRecordLoading(false);
            }
        };
        loadPlayRecordStats();
    }, []);

    useEffect(() => {
        const loadStatsByState = async () => {
            setStatsByStateLoading(true);
            try {
                const data = await ApiService.getData("/v1/stats");
                let englishStatsObj = null;
                let frenchStatsObj = null;
                if (data?.byLanguage) {
                    englishStatsObj = data.byLanguage.english ?? null;
                    frenchStatsObj = data.byLanguage.french ?? null;
                } else {
                    const gridStats = data?.grid ?? data?.gridTpl ?? {};
                    const crossStats = data?.cross ?? data?.crossTpl ?? {};
                    const gridByLang = gridStats?.byLanguage || {};
                    const crossByLang = crossStats?.byLanguage || {};
                    const buildStatsForLang = (lang) => {
                        const g = gridByLang[lang] || {};
                        const c = crossByLang[lang] || {};
                        const langBlock = data?.byLanguage?.[lang] || {};
                        return {
                            gridWordSearchByState: g.gridWordSearchByState ?? langBlock.gridWordSearchByState,
                            gridHiddenMessageByState: g.gridHiddenMessageByState ?? langBlock.gridHiddenMessageByState,
                            crossByState: c.crossByState ?? langBlock.crossByState,
                            slidingByState: langBlock.slidingByState,
                            jigsawByState: langBlock.jigsawByState,
                            wordWheelByState: langBlock.wordWheelByState,
                            standaloneImageByState: langBlock.standaloneImageByState,
                        };
                    };
                    englishStatsObj = buildStatsForLang("english");
                    frenchStatsObj = buildStatsForLang("french");
                }
                setEnglishStats(englishStatsObj);
                setFrenchStats(frenchStatsObj);
                setPublishDateRanges(Array.isArray(data?.publishDateRanges) ? data.publishDateRanges : []);
            } catch (err) {
                Logger.error("Error loading stats by state:", err);
                setEnglishStats(null);
                setFrenchStats(null);
                setPublishDateRanges([]);
            } finally {
                setStatsByStateLoading(false);
            }
        };
        loadStatsByState();
    }, []);

    const loadSessionCount = async () => {
        try {
            setLoadingSessions(true);
            const data = await ApiService.getData("/v1/system/sessions/count");
            setSessionData(data);
        } catch (err) {
            Logger.error("Error loading session count:", err);
        } finally {
            setLoadingSessions(false);
        }
    };

    return (
        <Grid container spacing={2} sx={{ p: 2 }}>
            {/* Header Section */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Admin
                        </Typography>
                        <Typography variant="body1" color="text.secondary" >
                            Overview and today&apos;s imports
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1.5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={hideFrench}
                                    onChange={(e) => {
                                        const next = e.target.checked;
                                        setHideFrench(next);
                                        try {
                                            localStorage.setItem(HIDE_FRENCH_KEY, String(next));
                                        } catch {
                                            /* ignore */
                                        }
                                    }}
                                />
                            }
                            label="Hide French"
                            sx={{ mr: 0 }}
                        />
                        {!loadingSessions && sessionData && (
                            <Box sx={{ textAlign: "right" }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Active Sessions:{" "}
                                    <Typography component="span" variant="body2" sx={{ fontWeight: "bold" }}>
                                        {sessionData.activeSessions || 0}
                                    </Typography>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Active Users:{" "}
                                    <Typography component="span" variant="body2" sx={{ fontWeight: "bold" }}>
                                        {sessionData.activeUsers || 0}
                                    </Typography>
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>

            {/* Overview Summary Cards (Today's Imports, etc.) */}
            <Grid size={{ xs: 12 }}>
                <PuzzleOverview hideFrench={hideFrench} />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <EditorsPicksOverview />
            </Grid>

            {/* Play opens by puzzle type (ENTER events) */}
            <Grid size={{ xs: 12 }}>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                            Play opens
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            ENTER events only, grouped by puzzle type
                        </Typography>
                        {playRecordLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading play record stats…
                                </Typography>
                            </Box>
                        ) : playRecordStats ? (
                            <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                                <Table aria-label="Play opens by puzzle type" size="small" sx={{ minWidth: 520 }}>
                                    <TableHead>
                                        <TableRow sx={{ "& .MuiTableCell-root": { py: 2 } }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                                            {TYPE_ORDER.map((type) => (
                                                <TableCell key={type} align="right" sx={{ fontWeight: 600 }}>
                                                    {TYPE_LABELS[type] ?? type}
                                                </TableCell>
                                            ))}
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {[
                                            { key: "today", label: "Today", data: playRecordStats.today },
                                            { key: "thisWeek", label: "This week", data: playRecordStats.thisWeek },
                                        ].map(({ key, label, data }) => (
                                            <TableRow key={key} sx={{ "& .MuiTableCell-root": { py: 2 } }}>
                                                <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                                                {TYPE_ORDER.map((type) => (
                                                    <TableCell key={type} align="right">
                                                        {Number(data?.[type]) || 0}
                                                    </TableCell>
                                                ))}
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                    {sumPlayRecordCounts(data)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Could not load play record stats.
                            </Typography>
                        )}
                        {playRecordStats?.wordWheelPlaySessions ? (
                            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                    component={RouterLink}
                                    to="/admin/puzzle/word_wheel_play"
                                    variant="outlined"
                                    size="small"
                                >
                                    Word wheel play sessions (
                                    {Number(playRecordStats.wordWheelPlaySessions.thisWeek) || 0} this week)
                                </Button>
                            </Box>
                        ) : null}
                    </CardContent>
                </Card>
            </Grid>

            {/* Statistics by state (same as /creator) */}
            <Grid size={{ xs: 12 }}>
                {statsByStateLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">Loading statistics by state...</Typography>
                    </Box>
                ) : (englishStats || (!hideFrench && frenchStats)) ? (
                    <StatsByStateTable
                        englishStats={englishStats}
                        frenchStats={hideFrench ? null : frenchStats}
                        hideFrench={hideFrench}
                        title="Statistics by state"
                        linkToAdminBoards
                    />
                ) : null}
            </Grid>

            {/* Publish date range by type and language (Type column merged) */}
            {publishDateRanges.length > 0 && (() => {
                const ranges = hideFrench
                    ? publishDateRanges.filter((r) => r.language !== "french")
                    : publishDateRanges;
                const ordered = orderedPublishDateRanges(ranges);
                if (ordered.length === 0) return null;
                let typeRowSpan = 0;
                let lastType = null;
                return (
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Publish date range
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table aria-label="Publish date range by type and language" sx={{ "& .MuiTableCell-root": { py: 2 } }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Earliest publish</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Latest publish</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ordered.map((row, idx) => {
                                        const isFirstOfType = row.type !== lastType;
                                        if (isFirstOfType) {
                                            lastType = row.type;
                                            typeRowSpan = ordered.filter((r) => r.type === row.type).length;
                                        }
                                        return (
                                            <TableRow key={`${row.type}-${row.language}-${idx}`}>
                                                {isFirstOfType ? (
                                                    <TableCell rowSpan={typeRowSpan} sx={{ verticalAlign: "top", fontWeight: 600 }}>
                                                        {TYPE_LABELS[row.type] ?? row.type}
                                                    </TableCell>
                                                ) : null}
                                                <TableCell>{LANG_LABELS[row.language] ?? row.language ?? "—"}</TableCell>
                                                <TableCell sx={{ whiteSpace: "normal" }}>
                                                    {(() => {
                                                        const { line1, line2 } = formatPublishDateWithHumanize(row.earliestPublishAt);
                                                        return (
                                                            <Box sx={{ display: "block" }}>
                                                                <Box component="span" sx={{ display: "block", fontWeight: "bold" }}>{line1}</Box>
                                                                {line2 && <Box component="span" sx={{ display: "block", fontSize: "0.9em", color: "text.secondary" }}>{line2}</Box>}
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: "normal" }}>
                                                    {(() => {
                                                        const { line1, line2 } = formatPublishDateWithHumanize(row.latestPublishAt);
                                                        return (
                                                            <Box sx={{ display: "block" }}>
                                                                <Box component="span" sx={{ display: "block", fontWeight: "bold" }}>{line1}</Box>
                                                                {line2 && <Box component="span" sx={{ display: "block", fontSize: "0.9em", color: "text.secondary" }}>{line2}</Box>}
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                );
            })()}
        </Grid>
    );
}